import { TeamStatus, EnrollmentStatus } from '@sulis-liiga/common';
import type { Team } from '@sulis-liiga/common';

/**
 * The unified team lifecycle presented across the app. A team is always in
 * exactly one of four states — there is no "not enrolled" state, because
 * enrollment is integrated into team creation:
 *
 *   Create (enrolling integrated)
 *     → Awaiting Partner Response   (team PENDING_PARTNER)
 *     → Awaiting Enrollment         (team ACTIVE, no placed enrollment yet)
 *     → Enrolled                    (team ACTIVE + a placed/ACTIVE enrollment)
 *     → Withdrawn                   (team WITHDRAWN or CANCELLED)
 *
 * A doubles/mixed team is created together with its enrollments, but those
 * enrollments stay hidden from the admin placement queue until the partner
 * accepts — hence "Awaiting Partner Response" comes before "Awaiting Enrollment".
 *
 * Returns a single badge label + Tailwind class so Home, My Teams, the team
 * detail page, and TeamCard all render the same status.
 */
export function getTeamLifecycle(team: Team): { label: string; className: string } {
  if (team.status === TeamStatus.PENDING_PARTNER) {
    return { label: 'Awaiting Partner Response', className: 'bg-yellow-100 text-yellow-800' };
  }
  // A cancelled (partner declined) team is presented as withdrawn — both mean the
  // team is no longer in play.
  if (team.status === TeamStatus.WITHDRAWN || team.status === TeamStatus.CANCELLED) {
    return { label: 'Withdrawn', className: 'bg-gray-100 text-gray-600' };
  }

  // Team is ACTIVE — the state now depends on its enrollments.
  const enrollments = team.enrollments ?? [];
  if (enrollments.some(e => e.status === EnrollmentStatus.ACTIVE)) {
    return { label: 'Enrolled', className: 'bg-success-100 text-success-800' };
  }
  if (enrollments.some(e => e.status === EnrollmentStatus.PENDING_PLACEMENT)) {
    return { label: 'Awaiting Enrollment', className: 'bg-yellow-100 text-yellow-800' };
  }
  // Every enrollment is withdrawn (e.g. an admin withdrew the team) — reflect that
  // back to the player. A COMPLETED enrollment (a finalised past season) is history,
  // not a withdrawal, so a team between seasons must NOT read "Withdrawn" — it falls
  // through to "Awaiting Enrollment" (it needs to enroll for the next season).
  if (enrollments.length > 0 && enrollments.every(e => e.status === EnrollmentStatus.WITHDRAWN)) {
    return { label: 'Withdrawn', className: 'bg-gray-100 text-gray-600' };
  }
  return { label: 'Awaiting Enrollment', className: 'bg-yellow-100 text-yellow-800' };
}
